import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sparkles,
  ShieldCheck,
  Clock,
  Star,
  CheckCircle2,
  ArrowRight,
  Home,
  Building2,
  BedDouble,
  HardHat,
  Phone,
  ChevronRight
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1707999495791-1c43efaea0b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHx0b3JvbnRvJTIwc2t5bGluZSUyMHN1bnNldHxlbnwwfHx8fDE3NzEwNzc0NzN8MA&ixlib=rb-4.1.0&q=85&w=1920";

const services = [
  {
    icon: Home,
    title: "Residential Cleaning",
    description: "Transform your home into a pristine sanctuary with our meticulous residential cleaning services.",
    image: "https://images.unsplash.com/photo-1630699144035-c0f6311ec482?crop=entropy&cs=srgb&fm=jpg&w=800",
    link: "/services#residential"
  },
  {
    icon: Building2,
    title: "Commercial Cleaning",
    description: "Maintain a professional, spotless workplace that impresses clients and boosts productivity.",
    image: "https://images.unsplash.com/photo-1770992176317-382f51aff35d?crop=entropy&cs=srgb&fm=jpg&w=800",
    link: "/services#commercial"
  },
  {
    icon: BedDouble,
    title: "Airbnb Cleaning",
    description: "Quick turnaround cleaning ensuring 5-star reviews and happy guests every time.",
    image: "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&w=800",
    link: "/services#airbnb"
  },
  {
    icon: HardHat,
    title: "Post-Renovation",
    description: "Expert removal of construction dust and debris, revealing your beautiful new space.",
    image: "https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&w=800",
    link: "/services#renovation"
  }
];

const features = [
  {
    icon: Sparkles,
    title: "Premium Quality",
    description: "We use eco-friendly, high-quality products for exceptional results."
  },
  {
    icon: ShieldCheck,
    title: "Fully Insured",
    description: "Complete peace of mind with our comprehensive insurance coverage."
  },
  {
    icon: Clock,
    title: "Flexible Scheduling",
    description: "Book at your convenience, 7 days a week with easy rescheduling."
  },
  {
    icon: Star,
    title: "Satisfaction Guaranteed",
    description: "Not happy? We'll re-clean for free within 24 hours."
  }
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data.filter(t => t.featured).slice(0, 4));
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  return (
    <div data-testid="home-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Toronto Skyline"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl py-32">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4 animate-fade-in-up">
              Premium Cleaning Services in GTA
            </p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up delay-100">
              Elevate Your Space to{" "}
              <span className="gold-text">Perfection</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-10 animate-fade-in-up delay-200">
              Toronto Premium Cleaning delivers exceptional cleaning services for homes, offices, Airbnb properties, and post-renovation sites across the Greater Toronto Area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link to="/booking" data-testid="hero-book-btn">
                <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#C0A062]/30 transition-all hover:scale-[1.02]">
                  Book Your Cleaning
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/pricing" data-testid="hero-pricing-btn">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#064E3B] rounded-full px-8 py-6 text-lg font-medium transition-all">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/80 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="text-center p-8 rounded-xl hover:bg-[#F0FDF4] transition-colors duration-300"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#064E3B] flex items-center justify-center">
                  <feature.icon className="w-8 h-8 text-[#C0A062]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0C0A09] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#78716C] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-[#FAFAF9]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#064E3B] mb-4">
              Our Services
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0C0A09] mb-6">
              Professional Cleaning Solutions
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.link}
                data-testid={`service-card-${index}`}
              >
                <Card className="service-card group relative overflow-hidden bg-white border border-[#E7E5E4]/50 hover:border-[#C0A062]/50 h-full">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-[#064E3B]" />
                      </div>
                      <h3 className="text-2xl font-semibold text-[#0C0A09]">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-[#78716C] leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-[#064E3B] font-medium group-hover:text-[#C0A062] transition-colors">
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-32 bg-[#064E3B]">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <div className="text-center mb-16">
              <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
                Testimonials
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
                What Our Clients Say
              </h2>
              <div className="w-20 h-1 mx-auto bg-[#C0A062]" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="testimonial-card bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} className="w-5 h-5 text-[#C0A062] fill-[#C0A062]" />
                    ))}
                  </div>
                  <p className="text-white/90 leading-relaxed mb-6 italic">
                    "{testimonial.review}"
                  </p>
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-white/60 text-sm">{testimonial.location} • {testimonial.service}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/testimonials" data-testid="view-all-testimonials">
                <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium">
                  View All Reviews
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#F0FDF4]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0C0A09] mb-6">
              Ready for a Spotless Space?
            </h2>
            <p className="text-lg text-[#78716C] mb-10">
              Experience the Toronto Premium Cleaning difference. Book your service today and enjoy a cleaner, healthier environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking" data-testid="cta-book-btn">
                <Button className="bg-[#064E3B] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-emerald-900/20 transition-all hover:scale-[1.02]">
                  Book Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+16478700445" data-testid="cta-call-btn">
                <Button className="bg-transparent border-2 border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6 text-lg font-medium transition-all">
                  <Phone className="mr-2 w-5 h-5" />
                  Call (647) 870-0445
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white border-t border-[#E7E5E4]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-[#0C0A09] mb-2">
                Proudly Serving the Greater Toronto Area
              </h3>
              <p className="text-[#78716C]">
                Toronto • North York • Scarborough • Etobicoke • Mississauga • Brampton • Richmond Hill • Markham
              </p>
            </div>
            <Link to="/contact" data-testid="check-service-area">
              <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full">
                Check Your Area
                <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
